<template>
  <div class="quiz-results">
    <div v-if="isLoading" class="text-center py-4">
      <b-spinner class="mr-2" />
      Loading quiz results...
    </div>

    <div v-else-if="results" class="results-content">
      <!-- Results Summary -->
      <div class="results-summary mb-4">
        <div class="row">
          <div class="col-md-3">
            <div class="summary-card">
              <div class="summary-icon">
                <feather-icon icon="UsersIcon" size="24" />
              </div>
              <div class="summary-content">
                <div class="summary-number">{{ results.total_participants }}</div>
                <div class="summary-label">Total Participants</div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="summary-card">
              <div class="summary-icon">
                <feather-icon icon="TrendingUpIcon" size="24" />
              </div>
              <div class="summary-content">
                <div class="summary-number">{{ Math.round(results.average_score) }}%</div>
                <div class="summary-label">Average Score</div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="summary-card">
              <div class="summary-icon">
                <feather-icon icon="AwardIcon" size="24" />
              </div>
              <div class="summary-content">
                <div class="summary-number">{{ results.highest_score }}%</div>
                <div class="summary-label">Highest Score</div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="summary-card">
              <div class="summary-icon">
                <feather-icon icon="CheckCircleIcon" size="24" />
              </div>
              <div class="summary-content">
                <div class="summary-number">{{ Math.round(results.pass_rate) }}%</div>
                <div class="summary-label">Pass Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs for different views -->
      <b-tabs content-class="mt-3">
        <!-- Participants Tab -->
        <b-tab title="Participants" active>
          <div class="participants-table">
            <b-table
              :items="results.participants"
              :fields="participantFields"
              striped
              hover
              responsive
              show-empty
              empty-text="No participants found"
            >
              <template #cell(user_name)="{ item }">
                <div class="d-flex align-items-center">
                  <div class="participant-avatar mr-2">
                    {{ item.user_name.charAt(0).toUpperCase() }}
                  </div>
                  <span>{{ item.user_name }}</span>
                </div>
              </template>

              <template #cell(score)="{ item }">
                <div class="score-cell">
                  <span
                    class="badge badge-pill"
                    :class="getScoreClass(item.score)"
                  >
                    {{ Math.round(item.score) }}%
                  </span>
                  <div class="score-details text-muted text-sm">
                    {{ item.correct_answers }}/{{ item.total_questions }} correct
                  </div>
                </div>
              </template>

              <template #cell(time_spent)="{ item }">
                <span class="text-muted">{{ formatDuration(item.time_spent) }}</span>
              </template>

              <template #cell(submitted_at)="{ item }">
                <span class="text-muted">{{ formatDateTime(item.submitted_at) }}</span>
              </template>
            </b-table>
          </div>
        </b-tab>

        <!-- Question Analysis Tab -->
        <b-tab title="Question Analysis">
          <div class="question-analysis">
            <div class="question-analysis-header mb-3">
              <h6>Question Performance Analysis</h6>
              <p class="text-muted">
                Analyze how students performed on each question to identify areas that may need review.
              </p>
            </div>

            <div class="questions-list">
              <div
                v-for="(question, index) in results.question_analysis"
                :key="question.question_id"
                class="question-analysis-card"
              >
                <div class="question-header">
                  <div class="question-number">
                    Question {{ index + 1 }}
                  </div>
                  <div class="difficulty-badge">
                    <span
                      class="badge"
                      :class="getDifficultyClass(question.difficulty)"
                    >
                      {{ question.difficulty }}
                    </span>
                  </div>
                </div>

                <h6 class="question-title">{{ question.question_title }}</h6>

                <div class="question-stats">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="stat-item">
                        <div class="stat-number">{{ question.correct_count }}</div>
                        <div class="stat-label">Correct Answers</div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="stat-item">
                        <div class="stat-number">{{ question.total_answers }}</div>
                        <div class="stat-label">Total Answers</div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="stat-item">
                        <div class="stat-number">{{ Math.round(question.correct_percentage) }}%</div>
                        <div class="stat-label">Success Rate</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Progress bar for success rate -->
                <div class="success-progress mt-2">
                  <div class="progress">
                    <div
                      class="progress-bar"
                      :class="getProgressClass(question.correct_percentage)"
                      :style="{ width: question.correct_percentage + '%' }"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Export Tab -->
        <b-tab title="Export">
          <div class="export-options">
            <div class="export-header mb-3">
              <h6>Export Quiz Results</h6>
              <p class="text-muted">
                Download quiz results in different formats for further analysis.
              </p>
            </div>

            <div class="export-buttons">
              <b-button
                variant="outline-success"
                class="mr-2 mb-2"
                @click="exportResults('csv')"
                :disabled="isExporting"
              >
                <feather-icon icon="FileTextIcon" size="16" class="mr-1" />
                <b-spinner v-if="isExporting === 'csv'" small class="mr-1" />
                Export as CSV
              </b-button>

              <b-button
                variant="outline-primary"
                class="mr-2 mb-2"
                @click="exportResults('excel')"
                :disabled="isExporting"
              >
                <feather-icon icon="FileIcon" size="16" class="mr-1" />
                <b-spinner v-if="isExporting === 'excel'" small class="mr-1" />
                Export as Excel
              </b-button>

              <b-button
                variant="outline-danger"
                class="mr-2 mb-2"
                @click="exportResults('pdf')"
                :disabled="isExporting"
              >
                <feather-icon icon="FileTextIcon" size="16" class="mr-1" />
                <b-spinner v-if="isExporting === 'pdf'" small class="mr-1" />
                Export as PDF
              </b-button>
            </div>

            <div class="export-info mt-3">
              <div class="alert alert-info">
                <feather-icon icon="InfoIcon" size="16" class="mr-2" />
                <strong>Export includes:</strong> Participant scores, question analysis,
                timestamps, and overall quiz statistics.
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </div>

    <div v-else class="text-center py-4">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-muted mb-2" />
      <p class="text-muted">No results available for this quiz</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuizResults',

  props: {
    quizId: {
      type: [String, Number],
      required: true
    },
    webinarId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      results: null,
      isLoading: false,
      isExporting: false,
      participantFields: [
        {
          key: 'user_name',
          label: 'Student',
          sortable: true
        },
        {
          key: 'score',
          label: 'Score',
          sortable: true
        },
        {
          key: 'time_spent',
          label: 'Time Spent',
          sortable: true
        },
        {
          key: 'submitted_at',
          label: 'Submitted',
          sortable: true
        }
      ]
    }
  },

  async created() {
    await this.loadResults()
  },

  methods: {
    async loadResults() {
      this.isLoading = true
      try {
        const response = await this.$store.dispatch('lessonRoom/loadQuizResults', {
          webinarId: this.webinarId,
          quizId: this.quizId
        })
        this.results = response
      } catch (error) {
        console.error('Failed to load quiz results:', error)
        this.$bvToast.toast('Failed to load quiz results', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isLoading = false
      }
    },

    async exportResults(format) {
      this.isExporting = format
      try {
        // Import API directly
        const LessonRoomApi = (await import('@/api/lessonRoom')).default
        const response = await LessonRoomApi.exportQuizResults(
          this.webinarId,
          this.quizId,
          format
        )

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `quiz-results-${this.quizId}.${format}`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        this.$bvToast.toast(`Quiz results exported as ${format.toUpperCase()}`, {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        console.error('Failed to export results:', error)
        this.$bvToast.toast(`Failed to export as ${format}`, {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isExporting = false
      }
    },

    getScoreClass(score) {
      if (score >= 90) return 'badge-success'
      if (score >= 70) return 'badge-primary'
      if (score >= 50) return 'badge-warning'
      return 'badge-danger'
    },

    getDifficultyClass(difficulty) {
      return {
        'easy': 'badge-success',
        'medium': 'badge-warning',
        'hard': 'badge-danger',
        'unknown': 'badge-secondary'
      }[difficulty] || 'badge-secondary'
    },

    getProgressClass(percentage) {
      if (percentage >= 80) return 'bg-success'
      if (percentage >= 50) return 'bg-warning'
      return 'bg-danger'
    },

    formatDuration(seconds) {
      if (!seconds) return '--'
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },

    formatDateTime(timestamp) {
      return new Date(timestamp).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
.quiz-results {
  .results-summary {
    .summary-card {
      background: white;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 1rem;
      display: flex;
      align-items: center;
      height: 80px;

      .summary-icon {
        background: #f8f9fa;
        border-radius: 50%;
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #007bff;
        margin-right: 1rem;
      }

      .summary-content {
        .summary-number {
          font-size: 1.5rem;
          font-weight: 600;
          color: #495057;
          line-height: 1;
        }

        .summary-label {
          color: #6c757d;
          font-size: 0.875rem;
          margin-top: 0.25rem;
        }
      }
    }
  }

  .participants-table {
    background: white;
    border-radius: 8px;
    border: 1px solid #e9ecef;

    .participant-avatar {
      width: 32px;
      height: 32px;
      background: #007bff;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 0.875rem;
    }

    .score-cell {
      .score-details {
        font-size: 0.75rem;
        margin-top: 0.25rem;
      }
    }
  }

  .question-analysis {
    .question-analysis-card {
      background: white;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 1rem;

      .question-header {
        display: flex;
        align-items: center;
        justify-content: between;
        margin-bottom: 0.5rem;

        .question-number {
          background: #007bff;
          color: white;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          margin-right: 0.5rem;
        }

        .difficulty-badge {
          margin-left: auto;
        }
      }

      .question-title {
        color: #495057;
        margin-bottom: 1rem;
      }

      .question-stats {
        .stat-item {
          text-align: center;
          padding: 0.5rem;
          background: #f8f9fa;
          border-radius: 6px;

          .stat-number {
            font-size: 1.25rem;
            font-weight: 600;
            color: #007bff;
          }

          .stat-label {
            font-size: 0.75rem;
            color: #6c757d;
            text-transform: uppercase;
            font-weight: 500;
          }
        }
      }

      .success-progress {
        .progress {
          height: 6px;
          background-color: #e9ecef;
          border-radius: 3px;
        }
      }
    }
  }

  .export-options {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1.5rem;

    .export-buttons {
      .btn {
        min-width: 140px;
      }
    }
  }
}
</style>